/**
 * Provider Management API — /api/v2/providers
 *
 * Auth is handled via the shared token registered through registerTokenProvider
 * in api.ts.  Paths starting with /api/v2 are used as-is (no base URL prefix)
 * — the dev Vite proxy forwards them to the backend.
 */

import { authHeaders } from "@/services/api";
import type {
  ProviderCreate,
  ProviderOut,
  ProviderListResponse,
  SchemaAssignmentOut,
  ProviderUser,
  UniquenessCheckResponse,
} from "@/types/providers";

import { appConfig } from "@/config/app";

/**
 * Construct a full API v2 URL for the given path.
 * @param path - The path to the API endpoint.
 * @returns The full API v2 URL.
 */

function apiV2Url(path: string): string {
  return `${appConfig.api.v2BaseUrl}${path}`;
}
const BASE = apiV2Url("/providers");

/**
 * Turn a FastAPI error body into a human-readable string.
 * Handles structured activation-checklist failures (`detail: { message, reasons }`)
 * that would otherwise coerce to "[object Object]" via `new Error(detail)`.
 */
function formatProviderApiError(body: unknown, status: number): string {
  const record = (body ?? {}) as {
    detail?: unknown;
    message?: string;
  };
  const detail = record.detail ?? record.message;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === "object") {
    const structured = detail as { message?: string; reasons?: unknown };
    const message =
      typeof structured.message === "string" && structured.message.trim()
        ? structured.message
        : null;
    const reasons = Array.isArray(structured.reasons)
      ? structured.reasons.filter((r): r is string => typeof r === "string" && r.trim().length > 0)
      : [];

    if (message && reasons.length > 0) {
      return `${message} ${reasons.join("; ")}`;
    }
    if (message) return message;
    if (reasons.length > 0) return reasons.join("; ");
    try {
      return JSON.stringify(detail);
    } catch {
      return `HTTP ${status}`;
    }
  }

  return `HTTP ${status}`;
}

/** Thin wrapper around fetch that attaches the bearer token and throws on non-2xx. */
async function providerFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(path, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(formatProviderApiError(body, res.status));
  }

  // 204 No Content — return undefined cast to T
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

// ── List / fetch ──────────────────────────────────────────────────────────────

export interface FetchProvidersParams {
  search?: string;
  status?: string;
  providerType?: string;
  offset?: number;
  limit?: number;
}

/**
 * GET /api/v2/providers
 * Returns a paginated, filtered list of providers.
 */
export async function fetchProviders(
  params: FetchProvidersParams = {}
): Promise<ProviderListResponse> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.status) qs.set("status", params.status);
  if (params.providerType) qs.set("provider_type", params.providerType);
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return providerFetch<ProviderListResponse>(`${BASE}${query ? `?${query}` : ""}`);
}

let providerListCache: Promise<ProviderListResponse> | null = null;

/**
 * Session-cached provider list for read-only consumers (selectors, name
 * lookups, board filters): one fetch per session instead of one per mount.
 * Provider mutations in this module invalidate the cache so the next consumer
 * refetches. Callers needing server-side search/pagination (Provider
 * Management list) should keep using fetchProviders directly.
 */
export function fetchProvidersCached(): Promise<ProviderListResponse> {
  if (!providerListCache) {
    providerListCache = fetchProviders({ limit: 100 }).catch((err: unknown) => {
      // Never cache a failure — the next mount should retry.
      providerListCache = null;
      throw err;
    });
  }
  return providerListCache;
}

export function invalidateProvidersCache(): void {
  providerListCache = null;
}

// ── Single provider ────────────────────────────────────────────────────────────

/**
 * GET /api/v2/providers/{providerId}
 * Returns a single provider with schema assignments and last submission date.
 */
export async function fetchProvider(providerId: string): Promise<ProviderOut> {
  return providerFetch<ProviderOut>(`${BASE}/${providerId}`);
}

// ── Schema assignments ─────────────────────────────────────────────────────────

/**
 * GET /api/v2/providers/{providerId}/schema-assignments
 * Returns the active schema assignments for a provider.
 */
export async function fetchSchemaAssignments(providerId: string): Promise<SchemaAssignmentOut[]> {
  return providerFetch<SchemaAssignmentOut[]>(
    `${BASE}/${providerId}/schema-assignments?active_only=true`
  );
}

/**
 * DELETE /api/v2/providers/{providerId}/schema-assignments/{schemaId}
 * Soft-deactivates a schema assignment (sets is_active = false).
 */
export async function removeSchemaAssignment(providerId: string, schemaId: string): Promise<void> {
  await providerFetch<void>(`${BASE}/${providerId}/schema-assignments/${schemaId}`, {
    method: "DELETE",
  });
  // Cached ProviderOut items embed assigned_schemas (Schema Builder pills).
  invalidateProvidersCache();
}

// ── Provider users (E7 / I4 / I5) ─────────────────────────────────────────────

/**
 * GET /api/v2/providers/{providerId}/users
 * Returns users linked to this provider via kodex_user_provider_access.
 */
export async function fetchProviderUsers(providerId: string): Promise<ProviderUser[]> {
  return providerFetch<ProviderUser[]>(`${BASE}/${providerId}/users`);
}

/**
 * POST /api/v2/providers/{providerId}/users
 * Links a registered platform user to this provider.
 */
export async function linkProviderUser(
  providerId: string,
  userId: string,
  accessRole: string
): Promise<ProviderUser> {
  return providerFetch<ProviderUser>(`${BASE}/${providerId}/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, access_role: accessRole }),
  });
}

/**
 * DELETE /api/v2/providers/{providerId}/users/{userId}
 * Soft-deactivates a user-provider access link.
 */
export async function unlinkProviderUser(providerId: string, userId: string): Promise<void> {
  await providerFetch<void>(`${BASE}/${providerId}/users/${userId}`, {
    method: "DELETE",
  });
}

// ── Create / mutate ────────────────────────────────────────────────────────────

/**
 * POST /api/v2/providers
 * Creates a new provider with status PENDING.
 */
export async function createProvider(data: ProviderCreate): Promise<ProviderOut> {
  const created = await providerFetch<ProviderOut>(BASE, {
    method: "POST",
    body: JSON.stringify(data),
  });
  invalidateProvidersCache();
  return created;
}

/**
 * PATCH /api/v2/providers/{providerId}
 * Partially updates provider identity / settings fields.
 */
export async function updateProvider(
  providerId: string,
  data: Partial<ProviderCreate> & {
    fix_override_enabled?: boolean;
    auto_promote_to_gold?: boolean;
  }
): Promise<ProviderOut> {
  const updated = await providerFetch<ProviderOut>(`${BASE}/${providerId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  invalidateProvidersCache();
  return updated;
}

/**
 * DELETE /api/v2/providers/{providerId}
 * Hard-deletes an INACTIVE provider and cascades dependent rows.
 * Requires global App.ADMIN (PROVIDER_WRITE).
 */
export async function deleteProvider(providerId: string): Promise<void> {
  await providerFetch<void>(`${BASE}/${providerId}`, {
    method: "DELETE",
  });
  invalidateProvidersCache();
}

/**
 * POST /api/v2/providers/{providerId}/status
 * Transitions a provider's status (e.g. pending → active, active → inactive).
 */
export async function transitionProviderStatus(
  providerId: string,
  newStatus: "active" | "inactive" | "pending"
): Promise<ProviderOut> {
  const transitioned = await providerFetch<ProviderOut>(`${BASE}/${providerId}/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ new_status: newStatus }),
  });
  invalidateProvidersCache();
  return transitioned;
}

/**
 * POST /api/v2/providers/{providerId}/schema-assignments
 * Assigns a single schema to the provider.  Call once per schema ID.
 */
export async function addSchemaAssignment(providerId: string, schemaId: string): Promise<void> {
  await providerFetch<void>(`${BASE}/${providerId}/schema-assignments`, {
    method: "POST",
    body: JSON.stringify({ schema_id: schemaId }),
  });
  invalidateProvidersCache();
}

/**
 * GET /api/v2/providers/check-name?name={name}
 * Returns {available: true} when no existing provider uses this name.
 * Use on blur of the provider name field. Pass excludeId when editing.
 */
export async function checkProviderName(
  name: string,
  excludeId?: string
): Promise<UniquenessCheckResponse> {
  const qs = new URLSearchParams({ name });
  if (excludeId) qs.set("exclude_id", excludeId);
  return providerFetch<UniquenessCheckResponse>(`${BASE}/check-name?${qs}`);
}

/**
 * GET /api/v2/providers/check-short-code?code={code}
 * Returns {available: true} when no existing provider uses this short code.
 * Use on blur of the short code field. Pass excludeId when editing.
 */
export async function checkProviderShortCode(
  code: string,
  excludeId?: string
): Promise<UniquenessCheckResponse> {
  const qs = new URLSearchParams({ code });
  if (excludeId) qs.set("exclude_id", excludeId);
  return providerFetch<UniquenessCheckResponse>(`${BASE}/check-short-code?${qs}`);
}
